'use client';

import { Boolean, Match, Option } from 'effect';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

/**
 * Controls one megamenu item and its panel.
 * The tenant composes the trigger, panel, and content.
 * The row item defines containment for pointer and focus events.
 */

export type UseMegamenuDisclosureParameters = {
  /**
   * The delay before a pointer preview opens the panel.
   * Each tenant sets its own delay.
   */
  readonly openDelayMillis: number;
};

export type MegamenuDisclosure = {
  readonly isOpen: boolean;
  /** The state shared by the trigger, indicator, and panel. */
  readonly state: 'open' | 'closed';
  /**
   * Attach to `MegamenuItem` to include both the trigger and panel in containment checks.
   */
  readonly itemRef: RefObject<HTMLLIElement | null>;
  /** Opens a pointer preview after the configured delay. */
  readonly openAfterDelay: () => void;
  /** Cancels a pending preview and closes the panel unless a popup holds it open. */
  readonly closeNow: () => void;
  /** Toggles a disclosure trigger immediately and cancels any pending preview. */
  readonly toggleNow: () => void;
  /**
   * Attach to `MegamenuItem`'s `onClick` to close after enabled navigation link activation.
   */
  readonly closeOnAction: (event: { readonly target: EventTarget | null }) => void;
  /**
   * Attach to `MegamenuItem`'s `onBlur` to close when focus moves outside the item.
   */
  readonly closeWhenFocusLeaves: (event: { readonly relatedTarget: EventTarget | null }) => void;
  /**
   * Holds the panel open while a portalled control is active.
   * Forward the control's open state and change details through `onOpenChange`.
   * An option choice releases the hold without closing the panel.
   * A press outside the row item or Escape closes the panel after the control releases the hold.
   */
  readonly holdOpen: (
    held: boolean,
    details?: { readonly reason: string; readonly event?: Event }
  ) => void;
};

export const useMegamenuDisclosure = ({
  openDelayMillis,
}: UseMegamenuDisclosureParameters): MegamenuDisclosure => {
  const [isOpen, setIsOpen] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);
  const heldRef = useRef(false);

  // A close or toggle cancels the delayed pointer preview.
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const openAfterDelay = useDebounceCallback(open, openDelayMillis);

  const close = useCallback(() => {
    if (heldRef.current) return;

    openAfterDelay.cancel();
    setIsOpen(false);
  }, [openAfterDelay]);

  const contains = useCallback(
    (node: Node): boolean =>
      Option.exists(Option.fromNullishOr(itemRef.current), (item) => item.contains(node)),
    []
  );

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && contains(target)) return;
      close();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    // Capture reads the popup hold before option handlers release it.
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [close, contains]);

  return {
    isOpen,
    state: isOpen ? 'open' : 'closed',
    itemRef,
    openAfterDelay,
    closeNow: close,
    toggleNow: () => {
      openAfterDelay.cancel();
      setIsOpen(Boolean.not);
    },
    closeOnAction: (event) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        Option.exists(
          Option.fromNullishOr(target.closest('a')),
          (anchor) => anchor.getAttribute('aria-disabled') !== 'true'
        )
      ) {
        close();
      }
    },
    holdOpen: (held, details) => {
      heldRef.current = held;
      if (held) return;

      Option.match(Option.fromNullishOr(details), {
        onNone: () => undefined,
        onSome: ({ reason, event }) =>
          Match.value(reason).pipe(
            Match.when('outside-press', () => {
              if (
                Option.exists(
                  Option.fromNullishOr(event),
                  ({ target }) => target instanceof Node && contains(target)
                )
              ) {
                return;
              }
              close();
            }),
            Match.when('escape-key', close),
            Match.orElse(() => undefined)
          ),
      });
    },
    closeWhenFocusLeaves: (event) => {
      // The event supplies the next focus target before the browser updates activeElement.
      const next = event.relatedTarget;
      // A press on non-focusable panel content supplies no next focus target.
      // Outside presses already close through the document pointer handler.
      if (next instanceof Node && !contains(next)) close();
    },
  };
};
